import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    RavenService,
    type RavenService as RavenServiceType,
} from '@telecom/shared/services/raven-service/raven-service';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_FTO_REPORT_CREATE_REPORT } from '@/constants/track-events';
import { FtoProApi } from '@/domains/fto-pro/apis/fto-pro-api';
import type { FtoReportSample } from '@/domains/fto-pro/apis/types/fto-report';
import {
    FTO_REPORT_GENERATION_MAX_ATTEMPTS,
    FTO_REPORT_GENERATION_POLL_INTERVAL,
} from '../../constants/work-in-progress';
import type { WorkInProgressFtoExportService } from '../work-in-progress-fto-export-service';

/**
 * 创建、跟踪并导出 Home V2 FTO 报告。
 */
@injectable()
export class WorkInProgressFtoExportServiceImpl implements WorkInProgressFtoExportService {
    constructor(
        @inject(FtoProApi)
        private readonly _ftoProApi: FtoProApi,
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
    ) {}

    async exportReport(taskId: string): Promise<string> {
        const createdReport = await this._ftoProApi.createReport(taskId);
        const reportId = createdReport.report_id;
        if (!reportId) {
            throw new Error('FTO report creation did not return a report ID');
        }

        await this._trackReportCreation(taskId, reportId);
        if (createdReport.status === 'failed') {
            throw new Error('FTO report generation failed');
        }
        if (createdReport.status !== 'success') {
            await this._waitForReportGeneration(taskId, reportId);
        }

        const result = await this._ftoProApi.exportReportAsWord(reportId);
        return result.sign_url;
    }

    private _trackReportCreation(taskId: string, reportId: string): Promise<void> {
        return this._ravenService.track(TRACK_EVENT_FTO_REPORT_CREATE_REPORT, {
            otherParam: taskId,
            eventType: TRACK_EVENT_TYPE.FtoReport,
            sourceType: TRACK_EVENT_FTO_REPORT_CREATE_REPORT,
            eventDetailStr: { report: reportId },
        });
    }

    private async _waitForReportGeneration(taskId: string, reportId: string): Promise<void> {
        for (let attempt = 0; attempt < FTO_REPORT_GENERATION_MAX_ATTEMPTS; attempt += 1) {
            const report = await this._findCreatedReport(taskId, reportId);
            if (report?.status === 'success') return;
            if (report?.status === 'failed') {
                throw new Error('FTO report generation failed');
            }
            if (attempt < FTO_REPORT_GENERATION_MAX_ATTEMPTS - 1) {
                await this._waitForNextPoll();
            }
        }
        throw new Error('FTO report generation timed out');
    }

    private async _findCreatedReport(taskId: string, reportId: string): Promise<FtoReportSample | undefined> {
        const reportList = await this._ftoProApi.getReportList({
            task_id: taskId,
            page: 1,
            page_size: 10,
        });
        return reportList.list.find(report => report.report_id === reportId);
    }

    private _waitForNextPoll(): Promise<void> {
        return new Promise(resolve => window.setTimeout(resolve, FTO_REPORT_GENERATION_POLL_INTERVAL));
    }
}
